import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import {Person} from '../person/person';
import {PersonLogin, RoleName} from '../person/person-login';
import {PersonRole} from '../person/person-role';
import {Role} from '../person/role';
import {PersonLoginService} from '../services/person-login-service';
import {PersonService} from '../services/person-service';

import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';

@Component({
    selector: 'person-roles',
    templateUrl: './person-roles.component.html',
    styleUrls: ['./person-roles.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon]
})
export class PersonRolesComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly personService = inject(PersonService);
  private readonly router = inject(Router);
  readonly personId = input.required<string>({ alias: 'person-id' });


  public personLogin!: PersonLogin;
  private readonly person = signal<Person>(undefined!);
  public readonly assignedRoles = signal<Role[]>(undefined!);
  public readonly availableRoles = signal<Role[]>(undefined!);
  public readonly selectedRole = signal<Role>(undefined!);
  public readonly clickedAssignedRole = signal<boolean>(undefined!);
  public readonly clickedAvailableRole = signal<boolean>(undefined!);

  constructor
  () {
    this.selectedRole.set(new Role());
  }

  ngOnInit(): void {
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.personLogin) {
      this.router.navigate(['main']);
    } else if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.clickedAssignedRole.set(false);
      this.clickedAvailableRole.set(false);
      this.initialize();
    }
  }

  private initialize(): void {
    this.initializeFromRouteParams();
  }

  public addUserRole(): void {
    let personRole: PersonRole = new PersonRole();
    personRole.personId = this.person().personId;
    personRole.id = this.selectedRole().id;
    this.personService.addUserRole(personRole).subscribe(result => {
      this.loadRoles();
      this.selectedRole.set(new Role());
      this.clickedAvailableRole.set(false);
      this.clickedAssignedRole.set(false);
    });
  }

  public removeUserRole(): void {
    let personRole: PersonRole = new PersonRole();
    personRole.personId = this.person().personId;
    personRole.id = this.selectedRole().id;
    this.personService.removeUserRole(personRole).subscribe(result => {
      this.loadRoles();
      this.selectedRole.set(new Role());
      this.clickedAvailableRole.set(false);
      this.clickedAssignedRole.set(false);
    });
  }

  private loadRoles(): void {
    this.personService.getAssignedRoles(this.person().personId).subscribe(result => this.assignedRoles.set(result));
    this.personService.getAvailableRoles(this.person().personId).subscribe(result => this.availableRoles.set(result));
  }

  public selectAvailableRole(role: Role) {
    this.selectedRole.set(role);
    this.clickedAvailableRole.set(true);
    this.clickedAssignedRole.set(false);
  }

  public selectAssignedRole(role: Role) {
    this.selectedRole.set(role);
    this.clickedAssignedRole.set(true);
    this.clickedAvailableRole.set(false);
  }

  private backToPersonSearch(): void {
    this.router.navigate(['person']);
  }

  public backToPersonDetail(): void {
    this.router.navigate(['person/' + this.person().personId]);
  }


  private initializeFromRouteParams(): void {
    var personId: string = this.personId();
    if (personId && personId.length > 0) {
      this.personService.getPersonById(personId).subscribe(result => {
        if (this.personLoginService.hasRole(RoleName.IT)
          || (result && result.primaryOrgUnitId
            && result.primaryOrgUnitId === this.personLogin.person.primaryOrgUnitId)) {
          this.person.set(result);
          this.loadRoles();
        } else {
          this.backToPersonSearch();
        }
      });
    } else {
      this.backToPersonSearch();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['personId'].firstChange) {
      this.initializeFromRouteParams();
    }
  }
}
